import axios from 'axios';

/**
 * API composable for making HTTP requests
 * @returns {Object} API methods
 */
export function useApi() {
  // Base URL for API requests
  const baseURL = process.env.VUE_APP_API_URL || '/api';

  /**
   * Get authorization headers
   * @returns {Object} Headers object
   */
  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { 'x-auth-token': token } : {};
  };

  /**
   * Handle API errors
   * @param {Error} error - Axios error object
   * @throws {Error} Formatted error
   */
  const handleError = (error) => {
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.message || 'An error occurred';
      const err = new Error(message);
      err.response = error.response;
      throw err;
    } else if (error.request) {
      // Request made but no response
      throw new Error('No response from server. Please check your connection.');
    } else {
      // Something else happened
      throw new Error(error.message || 'An unexpected error occurred');
    }
  };

  /**
   * Make a GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Response data
   */
  const get = async (endpoint, params = {}) => {
    try {
      const response = await axios.get(`${baseURL}${endpoint}`, {
        headers: getHeaders(),
        params
      });
      return response.data;
    } catch (error) {
      handleError(error);
    }
  };

  /**
   * Make a POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @returns {Promise<Object>} Response data
   */
  const post = async (endpoint, data = {}) => {
    try {
      const response = await axios.post(`${baseURL}${endpoint}`, data, {
        headers: getHeaders()
      });
      return response.data;
    } catch (error) {
      handleError(error);
    }
  };

  /**
   * Make a PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @returns {Promise<Object>} Response data
   */
  const put = async (endpoint, data = {}) => {
    try {
      const response = await axios.put(`${baseURL}${endpoint}`, data, {
        headers: getHeaders()
      });
      return response.data;
    } catch (error) {
      handleError(error);
    }
  };

  /**
   * Make a DELETE request
   * @param {string} endpoint - API endpoint
   * @returns {Promise<Object>} Response data
   */
  const del = async (endpoint) => {
    try {
      const response = await axios.delete(`${baseURL}${endpoint}`, {
        headers: getHeaders()
      });
      return response.data;
    } catch (error) {
      handleError(error);
    }
  };

  /**
   * Make a PATCH request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @returns {Promise<Object>} Response data
   */
  const patch = async (endpoint, data = {}) => {
    try {
      const response = await axios.patch(`${baseURL}${endpoint}`, data, {
        headers: getHeaders()
      });
      return response.data;
    } catch (error) {
      handleError(error);
    }
  };

  return {
    get,
    post,
    put,
    del,
    delete: del, // Alias for delete
    patch
  };
}
